import React, { useState } from 'react';

const LoginForm = ({ onLogin, onSwitchToSignup, onSwitchToForgotPassword, isLoading = false }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('📝 FORM: handleSubmit called');
    
    if (!validateForm()) {
      console.log('📝 FORM: Validation failed');
      return;
    }

    console.log('📝 FORM: Form validation passed, calling onLogin');
    // Clear any previous errors
    setErrors({});
    
    try {
      await onLogin(formData);
      console.log('📝 FORM: onLogin completed successfully');
    } catch (error) {
      console.error('📝 FORM: onLogin failed with error:', error);
      setErrors({ submit: error.message || 'Login failed' });
    }
  };

  return (
    <div className="modal-body">
      <div className="modal-header">
        <h2 className="heading-2" style={{ 
          textAlign: 'center',
          marginBottom: 'var(--spacing-2)',
          background: 'var(--gradient-primary)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Welcome Back
        </h2>
        <p className="text-secondary" style={{ 
          textAlign: 'center',
          marginBottom: 'var(--spacing-6)'
        }}>
          Sign in to continue your safe travel journey
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label required">
            Email Address
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            disabled={isLoading}
            autoComplete="email"
            className={`form-input ${errors.email ? 'form-input-error' : ''}`}
            placeholder="Enter your email"
          />
          {errors.email && (
            <span className="form-error">
              {errors.email}
            </span>
          )}
        </div>

        <div className="form-group">
          <label className="form-label required">
            Password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              disabled={isLoading}
              autoComplete="current-password"
              className={`form-input ${errors.password ? 'form-input-error' : ''}`}
              style={{ paddingRight: '48px' }}
              placeholder="Enter your password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              disabled={isLoading}
              style={{
                position: 'absolute',
                right: 'var(--spacing-3)',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-muted)'
              }}
            >
              {showPassword ? '👁️' : '🔒'}
            </button>
          </div>
          {errors.password && (
            <span className="form-error">
              {errors.password}
            </span>
          )}
        </div>

        {errors.submit && (
          <div style={{
            backgroundColor: 'var(--color-error-light)',
            color: 'var(--color-error)',
            padding: 'var(--spacing-3)',
            borderRadius: 'var(--radius-lg)',
            marginBottom: 'var(--spacing-4)',
            fontSize: 'var(--font-size-sm)'
          }}>
            ⚠️ {errors.submit}
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className={`btn btn-primary w-full btn-lg ${isLoading ? 'opacity-60' : ''}`}
          style={{ 
            marginTop: 'var(--spacing-2)',
            background: isLoading ? 'var(--color-gray-400)' : 'var(--gradient-primary)'
          }}
        >
          {isLoading ? (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: 'var(--spacing-2)' }}>
                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2">
                  <animate attributeName="r" values="3;6;3" dur="1s" repeatCount="indefinite"/>
                  <animate attributeName="opacity" values="1;0;1" dur="1s" repeatCount="indefinite"/>
                </circle>
              </svg>
              Signing In...
            </>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      <div style={{
        textAlign: 'center',
        marginTop: 'var(--spacing-6)',
        paddingTop: 'var(--spacing-4)',
        borderTop: `1px solid var(--color-border-light)`
      }}>
        <p className="text-secondary text-small" style={{ marginBottom: 'var(--spacing-2)' }}>
          Don't have an account?
        </p>
        <button
          onClick={onSwitchToSignup}
          disabled={isLoading}
          className="btn btn-ghost text-small"
          style={{ 
            color: 'var(--color-primary)',
            textDecoration: 'underline',
            padding: 'var(--spacing-1) var(--spacing-2)'
          }}
        >
          Create Account
        </button>
      </div>

      <div style={{
        textAlign: 'center',
        marginTop: 'var(--spacing-2)'
      }}>
        <button
          onClick={onSwitchToForgotPassword}
          disabled={isLoading}
          className="btn btn-ghost text-small"
          style={{
            color: 'var(--color-text-muted)',
            textDecoration: 'underline',
            fontSize: 'var(--font-size-xs)',
            padding: 'var(--spacing-1)'
          }}
        >
          Forgot Password?
        </button>
      </div>
    </div>
  );
};

export default LoginForm;